import { useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const year = new Date().getFullYear();
const TEMPLATES = {
  admin: `codice_fiscale;numero_tessera;disciplina;categorie;data_emissione;data_scadenza;note\nXMPMRA90A01H501A;MSP-X-000001;KART;K-KZ2,K-SEN;;${year}-12-31;\n`,
  sector_manager: "codice_fiscale;numero_tessera;disciplina;categorie;note\nXMPMRA90A01H501A;MSP-X-000001;KART;K-KZ2,K-SEN;\n",
};

export function LicenseImportDialog({ open, onOpenChange, onDone }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const template = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + TEMPLATES[user.role]], { type: "text/csv;charset=utf-8" }));
    a.download = "modello-importazione-licenze.csv";
    a.click();
  };
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/licenses/import", fd, { timeout: 180000 });
      setReport(data);
      toast.success(`${data.created} licenze ${data.mode}`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setReport(null); setFile(null); } }}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="license-import-dialog">
        <DialogHeader><DialogTitle>Importazione massiva licenze</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          {isAdmin ? "Le licenze importate vengono rilasciate subito (attive, con numero e QR code)." : "Ogni riga diventa una richiesta di licenza in attesa di approvazione della sede nazionale. Puoi inserire solo atleti del tuo settore."}
          {" "}Atleta individuato tramite codice fiscale o numero tessera; disciplina e categorie con i codici (es. KART, K-KZ2) o i nomi, categorie separate da virgola.
        </p>
        <Button variant="outline" onClick={template} className="w-fit" data-testid="license-import-template-btn"><Download className="mr-2 h-4 w-4" />Scarica modello CSV</Button>
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-6 text-sm text-slate-600 transition-colors hover:border-blue-500">
          <Upload className="h-6 w-6 text-slate-400" />
          {file ? <span className="font-medium text-slate-900">{file.name}</span> : "Seleziona un file .csv o .xlsx"}
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="license-import-file-input" />
        </label>
        <Button onClick={submit} disabled={!file || busy} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="license-import-submit-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Avvia importazione"}
        </Button>
        {report && (
          <div className="space-y-2 text-sm" data-testid="license-import-report">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="rounded-lg bg-emerald-50 p-3"><div className="text-2xl font-bold text-emerald-700">{report.created}</div>{report.mode === "rilasciate" ? "Rilasciate" : "Richieste inoltrate"}</div>
              <div className="rounded-lg bg-red-50 p-3"><div className="text-2xl font-bold text-red-700">{report.errors.length}</div>Errori</div>
            </div>
            {report.created_names.map((n) => <div key={n} className="text-emerald-800">{n}</div>)}
            {report.errors.map((e) => <div key={e.riga} className="text-red-700">Riga {e.riga}: {e.errore}</div>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
